/**
 * Collapsible sidebar navigation with account/team selector,
 * module-based navigation links, and user menu. Icons mapped dynamically.
 */
import type { Module, ModuleGroup } from "@nucleus/shared-types";
import {
	Activity,
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	LogOut,
	Mail,
	Map,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Plug,
	Search,
	Settings,
	ShoppingCart,
	Sparkles,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AccountTeamSelector } from "./AccountTeamSelector";

import { useIsAccountAdmin } from "@/hooks/usePermission";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";



interface SidebarProps {
	modules: {
		groups: (ModuleGroup & { modules: Module[] })[];
		ungrouped: Module[];
	};
}

/**
 * Sidebar navigation component
 *
 * Layout:
 * - Logo at top
 * - Brand selector below logo
 * - Module navigation in middle (grouped with separators)
 * - User menu at bottom
 * - Super Admin link (only for super_admin role)
 * @param props - component props
 * @param props.modules - grouped and ungrouped modules for navigation
 */
export function Sidebar({ modules }: SidebarProps) {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, logout } = useAuthStore();
	const isAccountAdmin = useIsAccountAdmin();

	// Logout mutation - calls backend to clear httpOnly cookies
	const logoutMutation = trpc.auth.logout.useMutation({
		onSettled: () => {
			// Always clear client state and redirect, even on error
			logout();
			void navigate("/login", { replace: true });
		},
	});

	const iconMap: Record<string, typeof LayoutDashboard> = {
		Activity,
		BarChart,
		BarChart3,
		Bell,
		Box,
		Building,
		Calendar,
		ChartPie,
		CircleDollarSign,
		Cog,
		Database,
		FileText,
		Folder,
		Globe,
		Home,
		Inbox,
		LayoutDashboard,
		LineChart,
		Mail,
		Map,
		Megaphone,
		MessageSquare,
		Monitor,
		PieChart,
		Search,
		Settings,
		ShoppingCart,
		Sparkles,
		Target,
		TrendingUp,
		User,
		Users,
		Zap,
	};

	/**
	 * Renders a navigation link for a module
	 * @param module - the module to render a link for
	 */
	const renderModuleLink = (module: Module) => {
		const Icon = iconMap[module.icon ?? ""] ?? LayoutDashboard;
		const isActive = location.pathname.startsWith(`/m/${module.slug}`);

		return (
			<Link
				key={module.id}
				to={`/m/${module.slug}`}
				className={cn(
					"flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-bold transition-colors",
					isActive
						? "bg-sidebar-accent text-sidebar-accent-foreground"
						: "text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
				)}
			>
				<Icon className="h-4 w-4" />
				{module.name}
			</Link>
		);
	};

	return (
		<aside className="relative z-10 flex h-screen w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-lg">
			{/* Logo */}
			<div className="flex items-center justify-center px-4 py-2">
				<Link to="/">
					<img
						src="/rpai-logo.png"
						alt="RPAi"
					/>
				</Link>
			</div>

			{/* Account/Team Selector */}
			<div className="px-4 py-2">
				<AccountTeamSelector />
			</div>

			{/* RPA.com-style Divider */}
			<div className="px-4 py-2 flex">
				<svg width="42" height="16" viewBox="0 0 42 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M1 8H41" stroke="white" strokeWidth="2" strokeLinecap="round"/>
				</svg>
			</div>

			{/* Module Navigation */}
			<nav className="flex-1 overflow-y-auto p-4">
				<div className="space-y-1">
					{/* Ungrouped modules first */}
					{modules.ungrouped.map(renderModuleLink)}

					{/* Grouped modules */}
					{modules.groups.map((group) => (
						<div key={group.id}>
							{group.name && (
								<div className="mb-2 mt-4 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-muted">
									{group.name}
								</div>
							)}
							{group.modules.map(renderModuleLink)}
							{group.separatorAfter && (
								<div className="my-4 border-t border-sidebar-border" />
							)}
						</div>
					))}
				</div>
			</nav>



			{/* Separator Line (RPA.com style gradient) */}
			<div className="px-4 py-2">
				<div
					className="h-px w-full"
					style={{
						background:
							"radial-gradient(circle at 0% 50%, rgb(231, 226, 20), rgb(108, 188, 69) 34%, rgb(72, 159, 143) 67%, rgb(66, 199, 243))",
					}}
				/>
			</div>

			{/* Bottom Section */}
			<div className="p-4 space-y-2">
				{/* Connections Link - for account admins (integrations.manage capability) */}
				{isAccountAdmin && (
					<Link
						to="/connections"
						className={cn(
							"flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
							location.pathname === "/connections"
								? "bg-sidebar-accent text-sidebar-accent-foreground"
								: "text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
						)}
					>
						<Plug className="h-4 w-4" />
						Connections
					</Link>
				)}

				{/* Account Settings Link - for account admins and super admins */}
				{isAccountAdmin && (
					<Link
						to="/settings/account"
						className={cn(
							"flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
							location.pathname.startsWith("/settings/account")
								? "bg-sidebar-accent text-sidebar-accent-foreground"
								: "text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
						)}
					>
						<Settings className="h-4 w-4" />
						Account Settings
					</Link>
				)}

				{/* Module Management Link - for account admins and brand admins */}
				{isAccountAdmin && (
					<Link
						to="/settings/modules"
						className={cn(
							"flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
							location.pathname === "/settings/modules"
								? "bg-sidebar-accent text-sidebar-accent-foreground"
								: "text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
						)}
					>
						<Box className="h-4 w-4" />
						Module Management
					</Link>
				)}

				{/* Super Admin Link */}
				{user?.role === "super_admin" && (
					<Link
						to="/admin"
						className={cn(
							"flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
							location.pathname.startsWith("/admin")
								? "bg-sidebar-accent text-sidebar-accent-foreground"
								: "text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
						)}
					>
						<Settings className="h-4 w-4" />
						Platform Setup
					</Link>
				)}

				{/* User Menu */}
				<div className="flex items-center gap-3 rounded-lg px-3 py-2">
					<div className="flex h-8 w-8 items-center justify-center rounded-full bg-sidebar-accent">
						<User className="h-4 w-4" />
					</div>
					<div className="flex-1 truncate">
						<div className="text-sm font-medium">{user?.name}</div>
						<div className="text-xs text-sidebar-muted">
							{user?.email}
						</div>
					</div>
					<button
						onClick={() => { logoutMutation.mutate(); }}
						disabled={logoutMutation.isPending}
						className="rounded p-1 hover:bg-sidebar-accent disabled:opacity-50"
						title="Logout"
					>
						<LogOut className="h-4 w-4" />
					</button>
				</div>				
			</div>
		</aside>
	);
}
